// =============================================================
// slider.js — circular looper navigation. GSAP transitions.
// Velocity-aware: hard swipes/wheel jump multiple slides ("spin").
// Both horizontal and vertical swipes navigate. Direction is
// determined by the dominant axis of the gesture.
// Public API:
//   new LooperSlider(track, slides, opts)
//     .next() / .prev() / .goTo(idx)
//     .spin(signedSteps)   // multi-step "spin" with chained transitions
// =============================================================

import { triggerGlitch } from "./glitch.js";

export class LooperSlider {
  constructor(track, slides, opts = {}) {
    this.track = track;
    this.slides = slides;
    this.total = slides.length;
    this.current = opts.start ?? 0;
    this.locked = false;
    this.duration = opts.duration ?? 0.85;
    this.onChange = opts.onChange || (() => {});
    // Visual axis remains horizontal — vertical input is mapped onto it.
    this._dirVertical = false;

    // Spin queue (sequential rapid transitions)
    this._spinRemaining = 0;
    this._spinSign = 1;

    // initial layout
    this._positionSlides(true);
    window.addEventListener("resize", () => {
      this._positionSlides(true);
    });
  }

  _positionSlides(immediate = false) {
    this.slides.forEach((s, i) => {
      const rel = this._relativeIndex(i, this.current);
      const offsetPercent = rel * 100;
      const transform = this._dirVertical
        ? `translate3d(0, ${offsetPercent}%, 0)`
        : `translate3d(${offsetPercent}%, 0, 0)`;

      if (window.gsap && !immediate) {
        window.gsap.to(s, { transform, duration: 0, overwrite: true });
      } else {
        s.style.transform = transform;
      }
      const isVisible = Math.abs(rel) <= 1;
      s.style.visibility = isVisible ? "visible" : "hidden";
      s.style.opacity = rel === 0 ? "1" : isVisible ? "1" : "0";
      s.classList.toggle("is-active", rel === 0);
    });
  }

  _relativeIndex(i, current) {
    return i - current;
  }

  /**
   * Single-step navigation. Optionally accepts a custom duration
   * (used by spin queue for short intermediate hops).
   */
  goTo(target, opts = {}) {
    if (this.locked) return false;
    target = ((target % this.total) + this.total) % this.total;
    if (target === this.current) {
      if (opts.onDone) opts.onDone();
      return false;
    }

    const direction = opts.direction || (target > this.current ? 1 : -1);
    const isLoopJump =
      (this.current === this.total - 1 && target === 0 && direction === 1) ||
      (this.current === 0 && target === this.total - 1 && direction === -1);

    const duration = opts.duration ?? this.duration;
    this.locked = true;

    const swap = () => {
      const prev = this.current;
      this.current = target;
      this._animate(prev, target, direction, duration, () => {
        this.locked = false;
        this.onChange(this.current, { isLoop: isLoopJump });
        if (opts.onDone) opts.onDone();
      });
    };

    // Skip glitch for fast intermediate hops in a spin (feels janky)
    if (isLoopJump && !opts.skipGlitch) {
      triggerGlitch({
        duration: 0.55,
        onMid: () => swap(),
      });
    } else {
      swap();
    }
    return true;
  }

  /**
   * Spin through N steps. signedSteps > 0 = forward, < 0 = backward.
   * Intermediate hops use a short duration; the final settle uses a
   * longer eased duration for a tactile "wheel" feel.
   */
  spin(signedSteps) {
    if (!signedSteps) return;
    const sign = signedSteps > 0 ? 1 : -1;
    const absSteps = Math.abs(signedSteps);

    // If currently spinning, just extend the queue (preserve sign — flipping
    // mid-spin would feel chaotic).
    if (this._spinRemaining > 0) {
      if (sign === this._spinSign) {
        this._spinRemaining = Math.max(this._spinRemaining, absSteps);
      }
      return;
    }
    this._spinSign = sign;
    this._spinRemaining = absSteps;
    this._processSpin();
  }

  _processSpin() {
    if (this._spinRemaining <= 0) return;
    const remaining = this._spinRemaining;
    const sign = this._spinSign;
    const isFinal = remaining === 1;

    // Duration easing: faster for intermediate, slower for the settle.
    // Total time roughly: (n-1)*0.18 + 0.6
    const duration = isFinal ? 0.6 : 0.22;

    const target = ((this.current + sign) % this.total + this.total) % this.total;

    // If locked from elsewhere, retry shortly
    if (this.locked) {
      setTimeout(() => this._processSpin(), 30);
      return;
    }

    const started = this.goTo(target, {
      direction: sign,
      duration,
      skipGlitch: !isFinal, // suppress glitch on intermediate loop crossings
      onDone: () => {
        this._spinRemaining--;
        if (this._spinRemaining > 0) this._processSpin();
      },
    });

    if (!started) this._spinRemaining = 0;
  }

  _animate(prevIdx, nextIdx, direction, duration, done) {
    const prevSlide = this.slides[prevIdx];
    const nextSlide = this.slides[nextIdx];

    const axis = this._dirVertical ? "y" : "x";
    const fromIn = direction > 0 ? "100%" : "-100%";
    const toOut = direction > 0 ? "-100%" : "100%";

    // Position the rest off-screen
    this.slides.forEach((s, i) => {
      if (i === prevIdx || i === nextIdx) return;
      s.style.visibility = "hidden";
      s.style.transform =
        axis === "x" ? `translate3d(200%,0,0)` : `translate3d(0,200%,0)`;
    });

    nextSlide.style.visibility = "visible";
    nextSlide.style.opacity = "1";

    if (window.gsap) {
      const tl = window.gsap.timeline({
        defaults: { duration, ease: "expo.inOut" },
        onComplete: () => {
          this._positionSlides(true);
          if (done) done();
        },
      });
      tl.fromTo(
        nextSlide,
        { [axis]: fromIn, scale: 0.96, opacity: 0.6 },
        { [axis]: "0%", scale: 1, opacity: 1 }
      ).to(
        prevSlide,
        { [axis]: toOut, scale: 0.96, opacity: 0.6 },
        "<"
      );
    } else {
      prevSlide.style.transform =
        axis === "x" ? `translate3d(${toOut},0,0)` : `translate3d(0,${toOut},0)`;
      nextSlide.style.transform = "translate3d(0,0,0)";
      this._positionSlides(true);
      if (done) done();
    }
  }

  next() { this.spin(1); }
  prev() { this.spin(-1); }
}

// =============================================================
// Velocity → step-count mapping. Tuned for natural feel.
// distance (px) and dt (ms). Returns positive integer.
// Sensitivity reduced 50%: thresholds doubled, min distance raised.
// =============================================================
function stepsFromGesture(distance, dt) {
  const absDist = Math.abs(distance);
  if (absDist < 56) return 0; // was 28

  const velocity = absDist / Math.max(1, dt); // px/ms
  // Tunable thresholds (doubled vs. previous):
  //  ~1.1  px/ms = casual swipe → 1 step
  //  ~2.4  px/ms = firm flick   → 2 steps
  //  ~4.4  px/ms = strong flick → 4 steps
  //  ~7.0  px/ms = hard flick   → 7 steps
  //  >10   px/ms = full spin    → 10+ (loops)
  if (velocity < 1.10) return 1;
  if (velocity < 2.40) return 2;
  if (velocity < 4.40) return 4;
  if (velocity < 7.00) return 7;
  if (velocity < 10.0) return 10;
  return 14; // full spin past one loop
}

// =============================================================
// Input bindings: keyboard, wheel, touch, buttons
// =============================================================
// Returns true when a modal viewer (PhotoSwipe) is open and the main slider
// should ignore navigation input. PhotoSwipe sets window.__pswpOpen and adds
// .pswp-open on <body> for CSS hooks; we check both for resilience.
const isModalOpen = () =>
  !!window.__pswpOpen || document.body.classList.contains("pswp-open");

export function bindInputs(slider) {
  const prevBtn = document.querySelector(".nav-prev");
  const nextBtn = document.querySelector(".nav-next");

  prevBtn?.addEventListener("click", () => { if (!isModalOpen()) slider.prev(); });
  nextBtn?.addEventListener("click", () => { if (!isModalOpen()) slider.next(); });

  // -----------------------------
  // Keyboard
  // -----------------------------
  window.addEventListener("keydown", (e) => {
    if (isModalOpen()) return;
    if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
    if (e.key === "ArrowRight" || e.key === "ArrowDown" || e.key === " ") {
      e.preventDefault();
      slider.next();
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      slider.prev();
    } else if (e.key === "Home") slider.goTo(0);
    else if (e.key === "End") slider.goTo(slider.total - 1);
  });

  // -----------------------------
  // Wheel — velocity-aware
  // -----------------------------
  let wheelAccum = 0;
  let wheelStartTs = 0;
  let wheelFlushTimer = 0;

  const flushWheel = () => {
    const dt = Date.now() - wheelStartTs;
    const steps = stepsFromGesture(wheelAccum * 0.5, dt);
    if (steps > 0) {
      const sign = wheelAccum > 0 ? 1 : -1;
      slider.spin(sign * steps);
    }
    wheelAccum = 0;
    wheelStartTs = 0;
  };

  window.addEventListener(
    "wheel",
    (e) => {
      if (isModalOpen()) return;
      const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (Math.abs(delta) < 4) return;

      // Allow scrolling within overflowing slide content
      const inActive = e.target.closest(".slide.is-active");
      if (inActive) {
        const canScrollDown = inActive.scrollTop + inActive.clientHeight < inActive.scrollHeight - 2;
        const canScrollUp = inActive.scrollTop > 2;
        if (delta > 0 && canScrollDown) return;
        if (delta < 0 && canScrollUp) return;
      }

      if (wheelStartTs === 0) wheelStartTs = Date.now();
      wheelAccum += delta;

      // Debounced flush after wheel stops
      clearTimeout(wheelFlushTimer);
      wheelFlushTimer = setTimeout(flushWheel, 90);
    },
    { passive: true }
  );

  // -----------------------------
  // Touch — dominant-axis + velocity
  // -----------------------------
  let tStartX = 0, tStartY = 0, tStartT = 0;
  let touchActive = false;

  window.addEventListener("touchstart", (e) => {
    if (isModalOpen()) { touchActive = false; return; }
    const t = e.changedTouches[0];
    tStartX = t.clientX;
    tStartY = t.clientY;
    tStartT = Date.now();
    touchActive = true;
  }, { passive: true });

  window.addEventListener("touchend", (e) => {
    if (!touchActive) return;
    touchActive = false;
    if (isModalOpen()) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - tStartX;
    const dy = t.clientY - tStartY;
    const dt = Date.now() - tStartT;
    if (dt > 900) return;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    const horizontal = absDx > absDy;
    const distance = horizontal ? dx : dy;

    // If vertical and the active slide can actually scroll, defer to native scroll
    if (!horizontal) {
      const active = document.querySelector(".slide.is-active");
      if (active) {
        const canScroll = active.scrollHeight > active.clientHeight + 4;
        if (canScroll) {
          // Only consume swipe if it's a strong flick
          const velocity = Math.abs(dy) / Math.max(1, dt);
          if (velocity < 1.3) return;
        }
      }
    }

    const steps = stepsFromGesture(distance, dt);
    if (steps === 0) return;

    // Sign convention:
    //  swipe LEFT  (dx < 0) → next   (sign +1)
    //  swipe RIGHT (dx > 0) → prev   (sign -1)
    //  swipe UP    (dy < 0) → next   (sign +1)
    //  swipe DOWN  (dy > 0) → prev   (sign -1)
    const sign = distance < 0 ? 1 : -1;
    slider.spin(sign * steps);
  }, { passive: true });

  window.addEventListener("touchcancel", () => { touchActive = false; }, { passive: true });
}
